import React, { useState } from 'react';
import { Shield, Fingerprint, ScanFace, X as CloseIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const getDeviceBiometricInfo = () => {
  if (typeof window === 'undefined') {
    return { name: 'Biometrics / Passkey', prompt: 'Use Biometrics / Passkey', icon: 'fingerprint' };
  }
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return {
      name: 'Face ID / Touch ID',
      prompt: 'Use Face ID / Touch ID',
      icon: 'face-id',
      hint: 'Authenticate with Apple Face ID, Touch ID, or device passcode',
    };
  }
  if (/Macintosh|Mac OS X/.test(ua)) {
    return {
      name: 'Touch ID / Passkey',
      prompt: 'Use Touch ID / Passkey',
      icon: 'fingerprint',
      hint: 'Authenticate with Touch ID or Mac Passkey',
    };
  }
  if (/Android/.test(ua)) {
    return {
      name: 'Fingerprint / Screen Lock',
      prompt: 'Use Fingerprint / Screen Lock',
      icon: 'fingerprint',
      hint: 'Authenticate with Android Fingerprint, Face, or Screen Lock',
    };
  }
  if (/Windows/.test(ua)) {
    return {
      name: 'Windows Hello / Passkey',
      prompt: 'Use Windows Hello / Passkey',
      icon: 'fingerprint',
      hint: 'Authenticate with Windows Hello (Face, Fingerprint, PIN, or Passkey)',
    };
  }
  return {
    name: 'Biometrics / Passkey',
    prompt: 'Use Biometrics / Passkey',
    icon: 'fingerprint',
    hint: 'Authenticate with device biometrics or passkey',
  };
};

export const SecurityAuthModal = ({ open, action = 'view', onClose, onSuccess }) => {
  const [masterPassword, setMasterPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const {
    reauthenticateWithMasterPassword,
    reauthenticateWithPasskey,
    registerPasskey,
  } = useAuth();

  if (!open) return null;

  const biometricInfo = getDeviceBiometricInfo();
  const actionLabel = action === 'copy' ? 'copy' : 'view';

  const handleClose = () => {
    setMasterPassword('');
    setError('');
    setBusy(false);
    onClose?.();
  };

  const handleBiometricAuth = async () => {
    setBusy(true);
    setError('');
    try {
      if (!window.PublicKeyCredential) {
        throw new Error('Biometric / Passkey authentication is not supported by this browser.');
      }
      const success = await reauthenticateWithPasskey();
      if (success) {
        handleClose();
        onSuccess?.();
        return;
      }
      setError(`No passkey registered on this device yet. Click "Set up on device" below or enter your master password.`);
    } catch (err) {
      setError(err?.message || 'Biometric verification failed. Please enter your master password.');
    } finally {
      setBusy(false);
    }
  };

  const handleRegisterBiometric = async () => {
    setBusy(true);
    setError('');
    try {
      if (!registerPasskey) {
        throw new Error('Passkey registration is unavailable.');
      }
      const success = await registerPasskey();
      if (success) {
        handleClose();
        onSuccess?.();
        return;
      }
      setError('Registration cancelled or not completed.');
    } catch (err) {
      setError(err?.message || 'Failed to set up passkey on this device.');
    } finally {
      setBusy(false);
    }
  };

  const handleMasterPasswordSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!masterPassword) {
      setError('Please enter your master password.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await reauthenticateWithMasterPassword(masterPassword);
      handleClose();
      onSuccess?.();
    } catch (authenticationError) {
      setError('Incorrect master password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(2, 6, 23, 0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '440px',
          background: 'var(--bg-surface, #ffffff)',
          border: '1px solid var(--border-color)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
        }}
      >
        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(59, 130, 246, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)' }}>
              <Shield size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: 'var(--text-primary)' }}>
                Security Verification
              </h3>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                Verify identity to {actionLabel} password
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleClose(); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}
            aria-label="Close verification dialog"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Option 1: Biometrics / Passkey */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            type="button"
            onClick={handleBiometricAuth}
            disabled={busy}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              width: '100%',
              padding: '13px 16px',
              borderRadius: '10px',
              border: '1.5px solid var(--accent-primary)',
              background: 'rgba(59, 130, 246, 0.08)',
              color: 'var(--accent-primary)',
              fontWeight: 600,
              fontSize: '14px',
              cursor: busy ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {biometricInfo.icon === 'face-id' ? <ScanFace size={20} /> : <Fingerprint size={20} />}
            <span>{biometricInfo.prompt}</span>
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              {biometricInfo.hint}
            </span>
            <button
              type="button"
              onClick={handleRegisterBiometric}
              disabled={busy}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent-primary)',
                fontSize: '11px',
                fontWeight: 600,
                cursor: busy ? 'not-allowed' : 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Set up on device
            </button>
          </div>
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '4px 0' }}>
          <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            or enter master password
          </span>
          <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
        </div>

        {/* Option 2: Master Password Form */}
        <form
          autoComplete="off"
          onSubmit={handleMasterPasswordSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          {/* Hidden anti-autofill decoy inputs */}
          <div style={{ display: 'none', position: 'absolute', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
            <input type="text" name="prevent_autofill_user" tabIndex={-1} autoComplete="off" />
            <input type="password" name="prevent_autofill_pwd" tabIndex={-1} autoComplete="off" />
          </div>

          <input
            type="password"
            name="pm_security_master_auth"
            id="pm_security_master_auth"
            className="input-field"
            value={masterPassword}
            onChange={(e) => setMasterPassword(e.target.value)}
            placeholder="Enter Master Password / Panel Password"
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: '10px',
              fontSize: '14px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
            }}
          />

          {error && (
            <div style={{ color: 'var(--danger, #ef4444)', fontSize: '13px', lineHeight: 1.4 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              style={{
                flex: 1,
                padding: '11px 16px',
                borderRadius: '10px',
                border: '1px solid var(--border-color)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontWeight: 500,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !masterPassword}
              style={{
                flex: 1.4,
                padding: '11px 16px',
                borderRadius: '10px',
                border: 'none',
                background: 'var(--accent-primary)',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '13px',
                cursor: busy || !masterPassword ? 'not-allowed' : 'pointer',
                opacity: busy || !masterPassword ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              {busy ? 'Verifying…' : `Verify & ${action === 'copy' ? 'Copy' : 'Reveal'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
